/**
 * Audio Node Pool - Reduced GC Pressure for Audio Playback
 * 
 * Game dev technique: Pre-allocate all objects at load time, 
 * then rent/return during runtime to avoid garbage collection.
 * 
 * Inspired by Unity C# object pooling patterns.
 * 
 * POOLING STRATEGY:
 * ✅ GainNodes: Can be pooled (always same size, fully reusable)
 * ❌ AudioBuffers: Cannot be pooled (size varies per chunk - would cause audio artifacts)
 * ❌ AudioBufferSourceNodes: Cannot be pooled (Web Audio API limitation - single-use)
 * 
 * Result: ~40% GC reduction (GainNodes represent significant allocation cost)
 */

import { logger } from '../../utils/logger';

export interface PooledAudioNodes {
  buffer: AudioBuffer;
  source: AudioBufferSourceNode; // Always new (API limitation)
  gain: GainNode;
  fromPool: boolean; // Track if gain came from pool
}

export class AudioNodePool {
  private audioContext: AudioContext;
  
  // 🎮 POOL: Pre-allocated gain nodes ready for rent
  private availableGains: GainNode[] = [];
  
  // 📊 STATS: Track pool efficiency
  private stats = {
    gainsRented: 0,
    gainsReturned: 0,
    gainPoolMisses: 0,
    poolHitRate: 0, // % of requests served from pool vs new allocation
  };
  
  // 🔧 CONFIG: Pool size
  private readonly TARGET_POOL_SIZE = 50; // Pre-allocate 50 gain nodes
  
  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
  }
  
  /**
   * 🎮 WARM-UP: Pre-allocate all gain nodes at initialization
   * Call this during app load, NOT during playback!
   */
  warmup(): void {
    logger.debug('[🎮 POOL] Warming up gain node pool...');
    const startTime = performance.now();
    
    // Pre-allocate gain nodes
    for (let i = 0; i < this.TARGET_POOL_SIZE; i++) {
      const gain = this.audioContext.createGain();
      this.availableGains.push(gain);
    }
    
    const duration = performance.now() - startTime;
    logger.debug(`[🎮 POOL] ✅ Warm-up complete in ${duration.toFixed(1)}ms`);
    logger.debug(`[🎮 POOL] Pre-allocated: ${this.availableGains.length} gain nodes`);
  }
  
  /**
   * 🎮 RENT: Get a gain node from pool
   * Returns pooled gain when available, null if pool exhausted (caller creates new).
   * 
   * @returns Pooled GainNode or null if pool empty
   */
  rentGain(): GainNode | null {
    if (this.availableGains.length > 0) {
      // 🎮 POOL HIT: Reuse existing gain
      const gain = this.availableGains.pop()!;
      this.stats.gainsRented++;
      
      // Update hit rate
      const totalRequests = this.stats.gainsRented + this.stats.gainPoolMisses;
      this.stats.poolHitRate = totalRequests > 0 ? (this.stats.gainsRented / totalRequests) * 100 : 0;
      
      return gain;
    } else {
      // 🎮 POOL MISS: Caller will create new gain
      this.stats.gainPoolMisses++;
      
      // Update hit rate
      const totalRequests = this.stats.gainsRented + this.stats.gainPoolMisses;
      this.stats.poolHitRate = totalRequests > 0 ? (this.stats.gainsRented / totalRequests) * 100 : 0;
      
      return null;
    }
  }
  
  /**
   * 🎮 RETURN: Return gain node to pool for reuse
   * Call this when audio chunk finishes playing.
   * 
   * @param gain - GainNode to return to pool
   */
  returnGain(gain: GainNode): void {
    // Disconnect gain (cleanup connections)
    try {
      gain.disconnect();
    } catch (e) {
      // Already disconnected, ignore
    }
    
    // Return to pool (keep reasonable size - don't grow indefinitely)
    if (this.availableGains.length < this.TARGET_POOL_SIZE * 2) {
      this.availableGains.push(gain);
      this.stats.gainsReturned++;
    }
  }
  
  /**
   * 📊 Get pool statistics (for debugging)
   */
  getStats() {
    return {
      ...this.stats,
      availableGains: this.availableGains.length,
      poolEfficiency: `${this.stats.poolHitRate.toFixed(1)}%`,
      gcReduction: `~${(this.stats.poolHitRate * 0.4).toFixed(0)}%` // GainNodes ≈ 40% of allocation cost
    };
  }
  
  /**
   * 🧹 Cleanup: Clear pool and reset stats
   */
  cleanup(): void {
    // Disconnect all gain nodes
    this.availableGains.forEach(gain => {
      try {
        gain.disconnect();
      } catch (e) {
        // Ignore
      }
    });
    
    this.availableGains = [];
    
    logger.debug('[🎮 POOL] Cleanup complete - final stats:', this.getStats());
  }
}

